import { useContext, useEffect } from 'react'

import { ConsultationContext, ConsultationContextProps } from '@/contexts/consultation'
import { useConsultation } from '@/hooks/useConsultation'
import { useModule } from '@/hooks/useModule'
import { useStep } from '@/hooks/useStep'
import { useTraces } from '@/hooks/useTraces'
import { useUser } from '@/hooks/useUser'
import { CONSULTATION_LANDING_STEP, CONSULTATION_STATUS, CONSULTATION_STEPS } from '@/types/consultation'
import { MODULE_TYPE } from '@/types/module'
import { User } from '@/types/user'
import { getInitialStep } from '@/utils/step'

const NEXT_STEP: Record<CONSULTATION_STEPS, CONSULTATION_STEPS> = {
  [CONSULTATION_STEPS.LANDING]: CONSULTATION_STEPS.FORM,
  [CONSULTATION_STEPS.FORM]: CONSULTATION_STEPS.MATCHING,
  [CONSULTATION_STEPS.MATCHING]: CONSULTATION_STEPS.MATCH,
  [CONSULTATION_STEPS.MATCH]: CONSULTATION_STEPS.ANSWER,
  [CONSULTATION_STEPS.ANSWER]: CONSULTATION_STEPS.LANDING
}

interface UseConsultationStepProps {
  currentConsultationStep: ConsultationContextProps['currentConsultationStep']
  updateStep: (step: CONSULTATION_STEPS) => void
  nextStep: () => void
}

export const useConsultationStep = (): UseConsultationStepProps => {
  const { currentConsultationStep, setCurrentConsultationStep } = useContext(ConsultationContext)
  const { currentModule } = useModule()
  const { currentStep, updateStep } = useStep()
  const { user } = useUser()
  const { addTrace, setTraces } = useTraces()
  const { isFollowUp, consultation } = useConsultation()

  const updateConsultationStep = (step: CONSULTATION_STEPS) => {
    setCurrentConsultationStep(step)
  }

  const nextStep = () => {
    switch (currentConsultationStep) {
      case CONSULTATION_STEPS.LANDING: {
        if (currentModule?.type !== MODULE_TYPE.CONSULTATION) return

        if (isFollowUp) return updateConsultationStep(CONSULTATION_STEPS.MATCHING)

        // Check if there are form steps
        if (currentModule.initialSteps.length > 0) {
          const initialStep = getInitialStep({
            module: currentModule,
            user: user as User
          })

          addTrace(currentModule.id, initialStep.id)
          updateStep(initialStep)
          updateConsultationStep(CONSULTATION_STEPS.FORM)
        }

        break
      }

      case CONSULTATION_STEPS.FORM: {
        // Remove all form traces
        setTraces((prev) => prev.filter((trace) => trace.moduleId !== currentModule?.id || trace.isFirstOfModule))
        updateConsultationStep(NEXT_STEP[currentConsultationStep])
        break
      }

      default: {
        console.warn('nextStep: default case', currentConsultationStep)
        updateConsultationStep(CONSULTATION_STEPS.LANDING)
      }
    }
  }

  useEffect(() => {
    if (!setCurrentConsultationStep) return

    if (
      currentStep?.id === CONSULTATION_LANDING_STEP.id &&
      [CONSULTATION_STATUS.CANCELED, CONSULTATION_STATUS.CLOSED, CONSULTATION_STATUS.CLOSED_WORATING].includes(
        consultation?.status as CONSULTATION_STATUS
      )
    ) {
      setCurrentConsultationStep(CONSULTATION_STEPS.LANDING)
    }
  }, [currentStep, setCurrentConsultationStep])

  return {
    currentConsultationStep,
    updateStep: updateConsultationStep,
    nextStep
  }
}
